import type { CompanyProfile, Quotation } from "@/api/types";
import { useTaxWords } from "@/app/session";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { PrintAmountInWords, PrintFooter, PrintHeader, PrintSignature } from "./DocumentSheet";

const NOT_FINAL: Partial<Record<Quotation["status"], string>> = {
  draft: "This is a draft. It has no number and has not been sent.",
  declined: "This quotation was declined by the customer.",
  expired: "The validity date of this quotation has passed.",
};

/** A quotation as printed: the invoice's layout, with its validity date and no payment details. */
export function QuotationPrint({
  quotation,
  seller,
}: {
  quotation: Quotation;
  seller: CompanyProfile | undefined;
}) {
  const say = useTaxWords();
  return (
    <article>
      <PrintHeader
        seller={seller}
        documentTitle={quotation.status === "draft" ? "Draft quotation" : "Quotation"}
        number={quotation.number}
        date={quotation.date}
        buyerLabel="Quoted to"
        buyerName={quotation.customerName}
        buyerTaxId={quotation.customerTaxId}
      />

      <p className="mb-3 text-sm">
        <span className="text-muted-foreground">Valid until </span>
        {formatDate(quotation.validUntil)}
        {quotation.reference ? (
          <>
            <span className="text-muted-foreground"> · Your reference </span>
            {quotation.reference}
          </>
        ) : null}
      </p>

      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">What is quoted</caption>
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className="text-muted-foreground py-1.5 text-left text-xs font-medium">
              Description
            </th>
            <th scope="col" className="text-muted-foreground py-1.5 text-right text-xs font-medium">
              Qty
            </th>
            <th scope="col" className="text-muted-foreground py-1.5 text-right text-xs font-medium">
              Unit price
            </th>
            <th scope="col" className="text-muted-foreground py-1.5 text-right text-xs font-medium">
              {say("VAT")}
            </th>
            <th scope="col" className="text-muted-foreground py-1.5 text-right text-xs font-medium">
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {quotation.lines.map((line) => (
            <tr key={line.n} className="border-muted border-b">
              <td className="text-foreground py-1.5">{line.itemName}</td>
              <td className="num text-muted-foreground py-1.5 text-right">{line.quantity}</td>
              <td className="num text-foreground py-1.5 text-right">
                {formatAmount(line.unitPrice)}
              </td>
              <td className="num text-muted-foreground py-1.5 text-right">{line.vatRatePct}%</td>
              <td className="num text-foreground py-1.5 text-right">
                {formatAmount(line.lineNet)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <dl className="ml-auto mt-4 w-64 text-sm">
        <Figure label="Net" value={quotation.taxExclusive} />
        {Number(quotation.allowances) !== 0 ? (
          <Figure label="Less allowances" value={quotation.allowances} />
        ) : null}
        {Number(quotation.charges) !== 0 ? (
          <Figure label="Charges" value={quotation.charges} />
        ) : null}
        <Figure label={say("VAT")} value={quotation.vat} />
        <Figure label="Total" value={quotation.total} emphasis />
      </dl>

      <PrintAmountInWords amount={quotation.total} currency={quotation.currency} />

      {quotation.terms ? (
        <section className="mt-4 text-sm">
          <h2 className="font-medium">Terms</h2>
          <p className="text-muted-foreground whitespace-pre-line">{quotation.terms}</p>
        </section>
      ) : null}

      <PrintSignature byLabel="Prepared by" />

      <PrintFooter note={NOT_FINAL[quotation.status] ?? quotation.note} />
    </article>
  );
}

function Figure({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div
      className={`flex items-baseline justify-between py-0.5 ${emphasis ? "border-border mt-1 border-t pt-1 font-semibold" : ""}`}
    >
      <dt className={emphasis ? "text-foreground" : "text-muted-foreground"}>{label}</dt>
      <dd className="num text-foreground">{formatAmount(value)}</dd>
    </div>
  );
}
